// Versões do Negociado (Rafael 23/10). "Nova versão do Negociado" congela a coluna Custos Negociados como
// está agora (Negociado V1, V2…, só leitura, à esquerda) e a coluna principal segue editável e é sempre a
// vigente: total, FEE, previsão e financeiro continuam lendo totalNegociado. Mesma estrutura da API
// (OrcamentoVersaoNegociado): orcamento.colunasNegociadoAdicionais, coluna com `congelada: true`.
// Coluna sem `congelada` = formato antigo (V2 editável que nada lia).

export const ehVersaoCongelada = (coluna) => !!coluna?.congelada;

export const versoesCongeladas = (colunas = []) => (colunas || []).filter(ehVersaoCongelada);

// O JSON chega como string (às vezes dentro de outra string) ou já como array.
export function lerColunasNegociado(valor) {
    let atual = valor;
    for (let i = 0; i < 3 && typeof atual === 'string'; i += 1) {
        try { atual = JSON.parse(atual); } catch (e) { return []; }
    }
    return Array.isArray(atual) ? atual.filter((c) => c && typeof c === 'object') : [];
}

// "Custos Negociados (V2)" quando já existe V1 congelada.
export function rotuloVersaoAtual(colunas = []) {
    const n = versoesCongeladas(colunas).length;
    return n > 0 ? `V${n + 1}` : null;
}

// Linha ainda não gravada não tem id: a versão não teria como apontar para ela.
export function linhasSemId(orcamentoitem = []) {
    return (orcamentoitem || []).reduce((total, item) => total
        + (item?.id ? 0 : 1)
        + (item?.subitem || []).filter((sub) => !sub?.id).length, 0);
}

export function motivoBloqueioNovaVersao(orcamentoitem = []) {
    const n = linhasSemId(orcamentoitem);
    if (n === 0) return null;
    return `Salve o orçamento antes: ${n === 1 ? 'há 1 linha nova' : `há ${n} linhas novas`} ainda não gravada${n === 1 ? '' : 's'}.`;
}

export function montarVersaoCongelada(orcamentoitem = [], colunas = [], { moedaDaLinha = () => 'real', agora = new Date(), id = null } = {}) {
    const n = versoesCongeladas(colunas).length + 1;
    return {
        id: id || `versao_${agora.getTime()}`,
        nome: `Negociado V${n}`,
        congelada: true,
        criadaEm: agora.toISOString(),
        itens: (orcamentoitem || []).map((item) => ({
            itemId: String(item.id),
            subitens: (item.subitem || []).map((sub) => ({
                subitemId: String(sub.id),
                produtoNome: sub?.produto?.nome || '',
                valorUnitario: sub.valorUnitarioNegociado ?? '',
                diaria: sub.diariaNegociado ?? '',
                quantidade: sub.quantidadeNegociado ?? '',
                servico: sub.servicoNegociado ?? '',
                // O total que a tela calculou (impostos, NF, FEE, R$ exato), não uma conta nova.
                total: sub.totalNegociado ?? 0,
                observacao: sub.observacaoNegociado ?? '',
                moeda: moedaDaLinha(sub),
                cambio: sub.cambioNegociado ?? null,
            })),
        })),
    };
}

export function valoresDaVersao(coluna, itemId, subitemId) {
    const item = (coluna?.itens || []).find((i) => String(i.itemId) === String(itemId));
    return (item?.subitens || []).find((s) => String(s.subitemId) === String(subitemId)) || null;
}

const numero = (v) => {
    if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
    const texto = String(v ?? '').trim();
    const n = Number(texto.includes(',') ? texto.replace(/\./g, '').replace(',', '.') : texto);
    return Number.isFinite(n) ? n : 0;
};

// Soma da versão só nas linhas que existem hoje no grupo (linha apagada depois sai da soma da tela).
export function totalDaVersaoNoGrupo(coluna, item) {
    return (item?.subitem || []).reduce((soma, sub) => soma + numero(valoresDaVersao(coluna, item?.id, sub?.id)?.total), 0);
}

export function totalDaVersao(coluna, orcamentoitem = []) {
    return (orcamentoitem || []).reduce((soma, item) => soma + totalDaVersaoNoGrupo(coluna, item), 0);
}

// Diária, quantidade e % serviço da versão: até 4 casas, sem zeros sobrando ("1", "7,48").
export function formatarNumeroVersao(valor) {
    if (valor === null || valor === undefined || String(valor).trim() === '') return '';
    return numero(valor).toLocaleString('pt-BR', { maximumFractionDigits: 4 });
}

// Título da versão com a data em que foi guardada ("Negociado V1 · 15/10"): o uso é acompanhar a evolução
// do orçamento semana a semana (Otávio 23/10).
export function rotuloVersao(coluna) {
    const nome = coluna?.nome || 'Negociado';
    const data = coluna?.criadaEm ? new Date(coluna.criadaEm) : null;
    if (!data || Number.isNaN(data.getTime())) return nome;
    return `${nome} · ${data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}`;
}

// Formato antigo (antes de 23/10): "Negociado V2" era uma coluna editável que nada lia. Converter = a última
// coluna antiga passa a ser o Negociado (só a última soma, Otávio 23/10); o Negociado de antes e as colunas
// antigas intermediárias viram versões congeladas, na ordem. A tela recalcula e a operadora salva.
export const colunasFormatoAntigo = (colunas = []) => (colunas || []).filter((c) => c && !ehVersaoCongelada(c));

const campoAntigo = (campo, colunaId) => `${campo}NegociadoAdicional_${colunaId}`;
const temValorAntigo = (sub, colunaId) => ['valorUnitario', 'diaria', 'quantidade', 'servico']
    .some((campo) => sub?.[campoAntigo(campo, colunaId)] !== undefined && sub?.[campoAntigo(campo, colunaId)] !== null && sub?.[campoAntigo(campo, colunaId)] !== '');

function versaoDaColunaAntiga(orcamentoitem, coluna, nome, criadaEm, moedaDaLinha) {
    return {
        id: `versao_${coluna.id}`,
        nome,
        congelada: true,
        criadaEm,
        itens: (orcamentoitem || []).map((item) => ({
            itemId: String(item.id),
            subitens: (item.subitem || []).filter((sub) => temValorAntigo(sub, coluna.id)).map((sub) => {
                const unit = numero(sub[campoAntigo('valorUnitario', coluna.id)]);
                const diaria = numero(sub[campoAntigo('diaria', coluna.id)]);
                const qtd = numero(sub[campoAntigo('quantidade', coluna.id)]);
                const servico = numero(sub[campoAntigo('servico', coluna.id)]);
                return {
                    subitemId: String(sub.id),
                    produtoNome: sub?.produto?.nome || '',
                    valorUnitario: sub[campoAntigo('valorUnitario', coluna.id)] ?? '',
                    diaria: sub[campoAntigo('diaria', coluna.id)] ?? '',
                    quantidade: sub[campoAntigo('quantidade', coluna.id)] ?? '',
                    servico: sub[campoAntigo('servico', coluna.id)] ?? '',
                    // A coluna antiga só fazia q × d × unit × (1 + serviço%): é o que ela mostrava.
                    total: Math.round(qtd * diaria * unit * (1 + servico / 100) * 100) / 100,
                    observacao: sub[campoAntigo('observacao', coluna.id)] ?? '',
                    moeda: moedaDaLinha(sub),
                    cambio: sub.cambioNegociado ?? null,
                };
            }),
        })),
    };
}

export function converterFormatoAntigo(orcamentoitem = [], colunas = [], { moedaDaLinha = () => 'real', agora = new Date() } = {}) {
    const antigas = colunasFormatoAntigo(colunas);
    if (antigas.length === 0) return null;
    const congeladas = versoesCongeladas(colunas);
    const quando = agora.toISOString();
    const historico = [montarVersaoCongelada(orcamentoitem, congeladas, { moedaDaLinha, agora, id: `versao_${agora.getTime()}` })];
    antigas.slice(0, -1).forEach((coluna) => {
        historico.push(versaoDaColunaAntiga(orcamentoitem, coluna, `Negociado V${congeladas.length + historico.length + 1}`, quando, moedaDaLinha));
    });
    const vigente = antigas[antigas.length - 1];
    const itens = JSON.parse(JSON.stringify(orcamentoitem || []));
    let linhasAlteradas = 0;
    itens.forEach((item) => (item.subitem || []).forEach((sub) => {
        if (!temValorAntigo(sub, vigente.id)) return; // linha incluída depois da V2: segue como está
        sub.valorUnitarioNegociado = sub[campoAntigo('valorUnitario', vigente.id)] ?? sub.valorUnitarioNegociado;
        sub.diariaNegociado = sub[campoAntigo('diaria', vigente.id)] ?? sub.diariaNegociado;
        sub.quantidadeNegociado = sub[campoAntigo('quantidade', vigente.id)] ?? sub.quantidadeNegociado;
        sub.servicoNegociado = sub[campoAntigo('servico', vigente.id)] ?? sub.servicoNegociado;
        const obs = sub[campoAntigo('observacao', vigente.id)];
        if (obs !== undefined && obs !== null && obs !== '') sub.observacaoNegociado = obs;
        linhasAlteradas += 1;
    }));
    return { orcamentoitem: itens, colunas: [...congeladas, ...historico], linhasAlteradas, nomeVigente: vigente.nome };
}

// Faixa do rodapé: avisa e oferece o botão (não trava o salvar).
export function avisoFormatoAntigo(colunas = []) {
    const antigas = colunasFormatoAntigo(colunas);
    if (antigas.length === 0) return [];
    const nomes = antigas.map((c) => c.nome).join(', ');
    return [{
        tipo: 'negociado_formato_antigo',
        mensagem: `${nomes}: coluna do jeito antigo, não soma no total. Ao converter, ${antigas[antigas.length - 1].nome} passa a valer como Custos Negociados e o Negociado de agora vira histórico (V1). Confira e salve.`,
        alvo: null,
        acao: { rotulo: `Converter ${antigas[antigas.length - 1].nome.replace('Negociado ', '')}` },
    }];
}
